import { useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { message } from '../utils/antdMessage';
import mapStateManager from '../utils/mapStateManager';
import { applyMapboxToken } from '../utils/mapboxToken';
import { isSecureGeolocationContext, getGeolocationHttpsHint } from '../utils/secureContext';
import { attachCoordAdapter, fromDisplay, DATUM_WGS84 } from '../utils/dashboard/basemapCoord';
import { ONLINE_TIMEOUT } from '../utils/rescuePush';
import { chromeStatusForMarkerState, getMemberIconPublicUrl } from '../modules/iconLibrary';

/**
 * 地图初始化、点击选点、队员标记、样式切换、定位
 */
export default function useRescuePushMap({
  mapContainer,
  mapRef,
  markersRef,
  rescueTargetMarkerRef,
  members,
  setTargetForm,
  loadPersistentDataRef,
  displayTargetOnMapRef,
  displayTrackOnMapRef,
  loadFromHistoryRef,
  savePersistentDataRef
}) {
  const [mapType, setMapType] = useState('mapbox://styles/mapbox/satellite-streets-v12');

  useEffect(() => {
    if (!mapContainer.current) return;

    console.log('开始地图初始化...');

    const dashboardMapState = mapStateManager.getMapState('dashboard');
    const defaultState = mapStateManager.getDefaultMapState();
    const mapState = dashboardMapState || defaultState;
    const mapStyle = mapStateManager.resolveUsableMapStyle(mapState.style);

    console.log('地图状态:', mapState, '实际样式:', mapStyle);

    applyMapboxToken(mapboxgl);

    mapRef.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      center: mapState.center || defaultState.center,
      zoom: mapState.zoom != null ? mapState.zoom : defaultState.zoom,
      attributionControl: false
    });
    // 推送页只用 Mapbox（WGS84）样式：显式声明，不沿用指挥页当前底图（否则在高德/谷歌下跳转过来点选会被错误反算）
    attachCoordAdapter(mapRef.current, { datum: DATUM_WGS84, basemapId: 'rescue-push-mapbox' });

    console.log('地图实例已创建:', mapRef.current);

    setMapType(mapStyle);

    mapRef.current.on('load', () => {
      console.log('地图加载完成');

      mapRef.current.resize();

      // 必须以服务端活跃列表为准：localStorage 可能仍残留已删/已完结轨迹，
      // 旧逻辑会 display + fitBounds，导致打开「救援推送」飞向幽灵线。
      loadPersistentDataRef.current();
      Promise.resolve(loadFromHistoryRef.current()).catch((err) => {
        console.error('救援推送打开对账失败:', err);
      });

      mapRef.current.on('click', (e) => {
        if (rescueTargetMarkerRef.current) {
          rescueTargetMarkerRef.current.remove();
          rescueTargetMarkerRef.current = null;
        }

        const displayLng = e.lngLat.lng;
        const displayLat = e.lngLat.lat;
        const wgs = fromDisplay(mapRef.current, displayLng, displayLat) || [displayLng, displayLat];

        const marker = new mapboxgl.Marker({
          draggable: false,
          color: '#ff0000'
        })
          .setLngLat([displayLng, displayLat])
          .addTo(mapRef.current);

        rescueTargetMarkerRef.current = marker;
        setTargetForm((f) => ({ ...f, lng: wgs[0].toFixed(6), lat: wgs[1].toFixed(6) }));

        marker.getElement().addEventListener('contextmenu', (evt) => {
          evt.preventDefault();
          marker.remove();
          rescueTargetMarkerRef.current = null;
          setTargetForm((f) => ({ ...f, lng: '', lat: '' }));
        });
      });
    });

    mapRef.current.on('styledata', () => {
      console.log('地图样式加载完成');
      mapRef.current.resize();
    });

    return () => {
      savePersistentDataRef.current();

      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, []);

  useEffect(() => {
    let timer = null;
    const checkTitleBarState = () => {
      if (!mapRef.current) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          const map = mapRef.current;
          if (!map || map._removed) return;
          if (!map.getCanvas?.() || !map.getContainer?.()?.isConnected) return;
          map.resize();
          console.log('RescuePush地图已重新调整大小，适应标题栏状态变化');
        } catch (_) {
          /* 地图已销毁时忽略 */
        }
      }, 100);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (mutation.target.classList.contains('no-title') || !mutation.target.classList.contains('no-title')) {
            checkTitleBarState();
          }
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || members.length === 0) return;

    console.log('开始更新地图标记，队员数量:', members.length);

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    const now = Date.now();

    members.forEach((member) => {
      if (member.last_longitude && member.last_latitude) {
        let isOnline = false;
        if (member.last_seen) {
          const t = new Date(member.last_seen).getTime();
          if (!isNaN(t) && now - t < ONLINE_TIMEOUT) isOnline = true;
        }
        const presence = chromeStatusForMarkerState(isOnline ? 'online' : 'offline');
        const iconUrl = getMemberIconPublicUrl(presence.iconKey);

        const popupHtml = `
          <div style='min-width:180px; font-size:14px; color:#222;'>
            <div><b>姓名：</b>${member.name || '未知'}</div>
            <div><b>小组：</b>${member.group_name || '未知'}</div>
            <div><b>组织：</b>${member.org_name || '未知'}</div>
            <div><b>经度：</b>${member.last_longitude != null ? member.last_longitude.toFixed(6) : '未知'}</div>
            <div><b>纬度：</b>${member.last_latitude != null ? member.last_latitude.toFixed(6) : '未知'}</div>
            <div><b>电量：</b>${member.battery_level != null ? member.battery_level + '%' : '未知'}</div>
            <div><b>海拔：</b>${member.last_altitude != null ? member.last_altitude + ' m' : '未知'}</div>
            <div><b>精度：</b>${member.last_accuracy != null ? member.last_accuracy + ' m' : '未知'}</div>
            <div><b>速度：</b>${member.last_speed != null ? member.last_speed + ' m/s' : '未知'}</div>
            <div><b>方向：</b>${member.last_bearing != null ? member.last_bearing + '°' : '未知'}</div>
            <div><b>GSM：</b>${member.gsm != null ? member.gsm : '未知'}</div>
            <div><b>上报时间：</b>${member.last_seen ? new Date(member.last_seen).toLocaleString() : '未知'}</div>
          </div>
        `;

        const el = document.createElement('img');
        el.src = iconUrl;
        el.style.width = '28px';
        el.style.height = '36px';
        el.style.objectFit = 'contain';
        el.style.background = 'none';
        el.style.display = 'block';
        el.style.border = 'none';
        el.style.padding = '0';
        el.title = member.name || '未知';

        const marker = new mapboxgl.Marker(el)
          .setLngLat([member.last_longitude, member.last_latitude])
          .setPopup(new mapboxgl.Popup().setHTML(popupHtml))
          .addTo(mapRef.current);

        el.addEventListener('click', (e) => {
          e.stopPropagation();
          console.log('点击了队员标记:', member.name);

          setTargetForm((f) => ({
            ...f,
            lat: member.last_latitude.toFixed(6),
            lng: member.last_longitude.toFixed(6),
            org_names: member.org_name ? [member.org_name] : [],
            group_names: member.group_name ? [member.group_name] : [],
            user_names: member.name ? [member.name] : []
          }));
        });

        markersRef.current.push(marker);
        console.log(`✅ 添加队员标记:`, member.name, '位置:', [member.last_longitude, member.last_latitude], '在线状态:', isOnline);
      }
    });

    console.log(`✅ 成功添加了 ${markersRef.current.length} 个队员标记`);
  }, [members]);

  const switchMapStyle = (styleId) => {
    if (!mapRef.current) return;

    console.log('切换地图样式:', styleId);

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    mapRef.current.setStyle(styleId);

    mapRef.current.once('styledata', () => {
      console.log('地图样式切换完成，重新添加队员标记...');

      const now = Date.now();

      members.forEach((member) => {
        if (member.last_longitude && member.last_latitude) {
          let isOnline = false;
          if (member.last_seen) {
            const t = new Date(member.last_seen).getTime();
            if (!isNaN(t) && now - t < ONLINE_TIMEOUT) isOnline = true;
          }
          const presence = chromeStatusForMarkerState(isOnline ? 'online' : 'offline');
          const iconUrl = getMemberIconPublicUrl(presence.iconKey);

          const popupHtml = `
            <div style='min-width:180px; font-size:14px; color:#222;'>
              <div><b>姓名：</b>${member.name || '未知'}</div>
              <div><b>小组：</b>${member.group_name || '未知'}</div>
              <div><b>组织：</b>${member.org_name || '未知'}</div>
              <div><b>经度：</b>${member.last_longitude != null ? member.last_longitude.toFixed(6) : '未知'}</div>
              <div><b>纬度：</b>${member.last_latitude != null ? member.last_latitude.toFixed(6) : '未知'}</div>
              <div><b>电量：</b>${member.battery_level != null ? member.battery_level + '%' : '未知'}</div>
              <div><b>海拔：</b>${member.last_altitude != null ? member.last_altitude + ' m' : '未知'}</div>
              <div><b>精度：</b>${member.last_accuracy != null ? member.last_accuracy + ' m' : '未知'}</div>
              <div><b>速度：</b>${member.last_speed != null ? member.last_speed + ' m/s' : '未知'}</div>
              <div><b>方向：</b>${member.last_bearing != null ? member.last_bearing + '°' : '未知'}</div>
              <div><b>GSM：</b>${member.gsm != null ? member.gsm : '未知'}</div>
              <div><b>上报时间：</b>${member.last_seen ? new Date(member.last_seen).toLocaleString() : '未知'}</div>
            </div>
          `;

          const el = document.createElement('img');
          el.src = iconUrl;
          el.style.width = '28px';
          el.style.height = '36px';
          el.style.objectFit = 'contain';
          el.style.background = 'none';
          el.style.display = 'block';
          el.style.border = 'none';
          el.style.padding = '0';
          el.title = member.name || '未知';

          const marker = new mapboxgl.Marker(el)
            .setLngLat([member.last_longitude, member.last_latitude])
            .setPopup(new mapboxgl.Popup().setHTML(popupHtml))
            .addTo(mapRef.current);

          el.addEventListener('click', (e) => {
            e.stopPropagation();
            console.log('点击了队员标记:', member.name);

            setTargetForm((f) => ({
              ...f,
              lat: member.last_latitude.toFixed(6),
              lng: member.last_longitude.toFixed(6),
              org_names: member.org_name ? [member.org_name] : [],
              group_names: member.group_name ? [member.group_name] : [],
              user_names: member.name ? [member.name] : []
            }));
          });

          markersRef.current.push(marker);
        }
      });

      console.log(`样式切换后重新添加了 ${markersRef.current.length} 个队员标记`);
    });
    setMapType(styleId);
  };

  const markTargetOnMap = (lat, lng, title = '目标位置') => {
    if (!mapRef.current || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    try {
      if (mapRef.current.getLayer('target-marker')) {
        mapRef.current.removeLayer('target-marker');
      }
      if (mapRef.current.getSource('target-marker')) {
        mapRef.current.removeSource('target-marker');
      }
      mapRef.current.addSource('target-marker', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: { title }
        }
      });
      mapRef.current.addLayer({
        id: 'target-marker',
        type: 'circle',
        source: 'target-marker',
        paint: {
          'circle-radius': 8,
          'circle-color': '#ff4444',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });
      mapRef.current.flyTo({ center: [lng, lat], zoom: 15 });
    } catch (error) {
      console.error('标记目标位置失败:', error);
    }
  };

  const flyToCurrentLocation = () => {
    if (!mapRef.current) {
      message.error('地图未初始化，无法定位');
      return;
    }

    if (!navigator.geolocation) {
      message.error('您的浏览器不支持地理定位功能');
      return;
    }

    if (!isSecureGeolocationContext()) {
      message.error(getGeolocationHttpsHint());
      return;
    }

    message.loading('正在获取位置信息...', 0);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { longitude, latitude, accuracy } = position.coords;
        message.destroy();

        const commanderMarkerEl = document.createElement('div');
        commanderMarkerEl.innerHTML = '🎯';
        commanderMarkerEl.style.cssText = `
          width: 40px;
          height: 40px;
          font-size: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
        `;

        if (window.rescueCommanderMarker) {
          window.rescueCommanderMarker.remove();
        }

        window.rescueCommanderMarker = new mapboxgl.Marker(commanderMarkerEl)
          .setLngLat([longitude, latitude])
          .setPopup(
            new mapboxgl.Popup().setHTML(`
            <div style='min-width:180px; font-size:14px; color:#222;'>
              <div><b>指挥员位置</b></div>
              <div><b>经度：</b>${longitude.toFixed(6)}</div>
              <div><b>纬度：</b>${latitude.toFixed(6)}</div>
              <div><b>精度：</b>${accuracy ? accuracy.toFixed(1) + '米' : '未知'}</div>
              <div><b>定位时间：</b>${new Date().toLocaleString()}</div>
            </div>
          `)
          )
          .addTo(mapRef.current);

        const pulseEl = document.createElement('div');
        pulseEl.style.cssText = `
          position: absolute;
          width: 60px;
          height: 60px;
          border: 3px solid #ff6b6b;
          border-radius: 50%;
          top: -10px;
          left: -10px;
          animation: pulse 2s ease-out infinite;
          pointer-events: none;
        `;
        commanderMarkerEl.appendChild(pulseEl);

        if (!document.querySelector('#pulse-animation-rescue')) {
          const style = document.createElement('style');
          style.id = 'pulse-animation-rescue';
          style.textContent = `
            @keyframes pulse {
              0% { transform: scale(0.8); opacity: 1; }
              100% { transform: scale(2); opacity: 0; }
            }
          `;
          document.head.appendChild(style);
        }

        mapRef.current.flyTo({
          center: [longitude, latitude],
          zoom: 14.5,
          duration: 2000,
          essential: true
        });

        message.success(`定位成功！精度: ${accuracy ? accuracy.toFixed(1) + '米' : '未知'}`);
      },
      (error) => {
        message.destroy();
        console.error('获取位置失败:', error);

        switch (error.code) {
          case error.PERMISSION_DENIED:
            message.error('位置权限被拒绝，请在浏览器设置中允许位置访问');
            break;
          case error.POSITION_UNAVAILABLE:
            message.error('位置信息不可用，请检查GPS是否开启');
            break;
          case error.TIMEOUT:
            message.error('获取位置超时，请重试');
            break;
          default:
            message.error('获取位置时发生未知错误');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000
      }
    );
  };

  return {
    mapType,
    switchMapStyle,
    markTargetOnMap,
    flyToCurrentLocation
  };
}
